import {
	type CollectionBrightnessCorrectionsJson,
	type CollectionCropParametersJson,
	type CollectionField as SchemaCollectionField,
	type CollectionFieldData,
	type CollectionImageVisibilityJson,
	type CollectionMeasurementsJson,
} from '@ikegps/core-api';
import type { CollectionModelBase } from '../collection.base';
import type { CollectionModel } from '../collection.data';
import type { CollectionModelField } from '../collection.field';
import { CollectionModelFieldSubForm } from '../fields/form.field';
import { CaptureProviderHelpers } from '../fields/provider/capture.provider';
import { BrightnessCorrectionWriter } from './collection.model.write.brightnessCorrection';
import { CropParameterWriter } from './collection.model.write.cropParameter';
import { DrawableWriter } from './collection.model.write.drawable';
import { FieldWriters } from './collection.model.write.field';
import { ImageVisibilityWriter } from './collection.model.write.imageVisibility';

const MEASUREMENT_STATE_TYPE = 'truesize';

export class IkeModelCollectionWriter {
	static writeField(collectionField: CollectionModelField): SchemaCollectionField {
		return FieldWriters[collectionField.schema.type](collectionField);
	}

	static writeData(instance: CollectionModelBase): CollectionFieldData {
		const instanceData = {
			_id: instance.instanceId,
		};

		for (const schemaField of instance.schema.fields) {
			if (!instance.hasField(schemaField.id)) {
				continue;
			}

			const field = instance.getField(schemaField.id);

			if (CollectionModelFieldSubForm.isSubFormField(field)) {
				(instanceData as { [key: string]: any })[schemaField.id] = field.value.map((ins) =>
					IkeModelCollectionWriter.writeData(ins)
				);
			} else if (field.hasValue()) {
				(instanceData as { [key: string]: any })[schemaField.id] = IkeModelCollectionWriter.writeField(field);
			}
		}

		return instanceData;
	}

	static writeCollectionMeasurements(model: CollectionModel): { [uuid: string]: CollectionMeasurementsJson } {
		const output: { [uuid: string]: CollectionMeasurementsJson } = {};
		const captures = CaptureProviderHelpers.getCaptureIds(model);
		for (const captureId of captures) {
			const capture = model.state.getCapture(captureId);
			if (capture == null) {
				continue;
			}

			const state = model.getDrawableState(captureId);
			for (const drawable of state.values()) {
				output[captureId] = output[captureId] || { state: [], type: MEASUREMENT_STATE_TYPE, captureId };
				if (output[captureId].state.find((existing) => existing.id === drawable.id)) {
					continue;
				}
				const val = DrawableWriter.write(model, drawable);
				if (val == null) {
					continue;
				}
				output[captureId].state.push(val);
				output[captureId].captureId = captureId;
			}
		}

		return output;
	}

	static writeCollectionBrightnessCorrections(model: CollectionModel): {
		[uuid: string]: CollectionBrightnessCorrectionsJson;
	} {
		const output: { [uuid: string]: CollectionBrightnessCorrectionsJson } = {};
		const captures = CaptureProviderHelpers.getCaptureIds(model);
		for (const captureId of captures) {
			const capture = model.state.getCapture(captureId);
			if (capture == null) {
				continue;
			}

			const state = model.getBrightnessCorrectionState(captureId);
			for (const brightnessCorrection of state.values()) {
				output[captureId] = output[captureId] || { state: [], type: capture.type, captureId };
				if (output[captureId].state.find((existing) => existing.id === brightnessCorrection.id)) {
					continue;
				}
				const val = BrightnessCorrectionWriter.write(brightnessCorrection);
				if (val == null) {
					continue;
				}
				output[captureId].state.push(val);
				output[captureId].captureId = captureId;
			}
		}

		return output;
	}

	static writeImageVisibilities(model: CollectionModel): { [uuid: string]: CollectionImageVisibilityJson } {
		const output: { [uuid: string]: CollectionImageVisibilityJson } = {};
		const captures = CaptureProviderHelpers.getCaptureIds(model);
		for (const captureId of captures) {
			const capture = model.state.getCapture(captureId);
			if (capture == null) {
				continue;
			}

			const state = model.getImageVisibilityState(captureId);

			if (state.hasImageVisibility()) {
				const imageVisibility = state.getImageVisibility();
				output[captureId] = output[captureId] || { state: undefined, type: capture.type, captureId };
				output[captureId].state = ImageVisibilityWriter.write(imageVisibility);
				output[captureId].captureId = captureId;
			}
		}

		return output;
	}

	static writeCollectionCropParameters(model: CollectionModel): { [uuid: string]: CollectionCropParametersJson } {
		const output: { [uuid: string]: CollectionCropParametersJson } = {};
		const captures = CaptureProviderHelpers.getCaptureIds(model);
		for (const captureId of captures) {
			const capture = model.state.getCapture(captureId);
			if (capture == null) {
				continue;
			}

			const state = model.getCropParameterState(captureId);
			for (const cropParameter of state.values()) {
				output[captureId] = output[captureId] || { state: [], type: capture.type, captureId };
				if (output[captureId].state.find((existing) => existing.id === cropParameter.id)) {
					continue;
				}
				const val = CropParameterWriter.write(cropParameter);
				if (val == null) {
					continue;
				}
				output[captureId].state.push(val);
				output[captureId].captureId = captureId;
			}
		}

		return output;
	}
}
